<template>
	<header
		class="app-header"
		:class="{
			'app-header--simplified': simplified,
			'app-header--wide': variant === 'wide',
		}"
	>
		<router-link :to="logoTo" class="app-header__logo">
			<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" v-if="simplified" v-bind:svg-inline="''" v-bind:class="'app-header__logo-simplfied'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M25.687 11.05a1.99 1.99 0 01.136-.748 1.83 1.83 0 01.367-.589c.16-.168.355-.298.571-.381.23-.092.475-.138.722-.137.505 0 .913.159 1.224.477.312.317.466.757.462 1.319a3.752 3.752 0 01-.394 1.62 8.437 8.437 0 01-.979 1.577c-.368.477-.778.92-1.225 1.324-.34.314-.709.596-1.101.841-.309.181-.59.272-.842.272a.696.696 0 01-.462-.136.496.496 0 01-.163-.408.718.718 0 01.218-.505c.144-.155.334-.351.57-.589.284-.292.547-.603.787-.931.342-.482.634-.997.87-1.538a1.48 1.48 0 01-.53-.53 1.742 1.742 0 01-.231-.938z" fill="#c1272d" fill-rule="nonzero"/><path d="M22.628 20.855c0 .308-.154.463-.464.463H11.706c-.309 0-.464-.154-.464-.463v-1.134c0-.297.156-.446.466-.446h3.654c.299 0 .448-.155.449-.465v-6.386c0-.298-.15-.447-.449-.447h-3.654c-.31 0-.466-.154-.466-.461v-1.123c0-.307.155-.46.464-.46h10.458c.31 0 .464.153.464.46v1.123c0 .307-.154.461-.464.461h-3.641c-.298 0-.446.149-.446.447v6.386c0 .31.148.465.446.465h3.641c.31 0 .464.149.464.446v1.134zM22.786 33.617a3.005 3.005 0 01-.987 2.293c-.659.612-1.464.918-2.413.918h-4.534a3.298 3.298 0 01-2.404-.987 3.23 3.23 0 01-.996-2.379 3.418 3.418 0 013.4-3.4h5.29c.252 0 .377-.126.377-.377v-.877a1.389 1.389 0 00-.337-.925 1.016 1.016 0 00-.799-.395h-2.831a.555.555 0 01-.386-.168.513.513 0 01-.18-.378v-.935a.67.67 0 01.566-.566h2.834a3.432 3.432 0 013.4 3.4v4.776zm-2.267 0v-1.099c0-.253-.126-.378-.377-.378h-5.29a1.023 1.023 0 00-.798.386 1.467 1.467 0 000 1.863c.19.248.485.394.798.395h4.534c.301.008.592-.107.807-.317.223-.225.341-.534.326-.85zM36.879 36.364c0 .309-.149.464-.447.464h-1.373c-.298 0-.446-.155-.446-.464v-7.779a1.392 1.392 0 00-.337-.936 1.039 1.039 0 00-1.614 0c-.225.26-.345.594-.337.936v3.23c0 .31-.149.464-.446.464h-1.37c-.298 0-.447-.154-.447-.464v-3.23a1.387 1.387 0 00-.337-.936 1.04 1.04 0 00-.816-.386c-.31.001-.605.143-.798.386-.225.26-.345.594-.337.936v7.779c0 .309-.149.464-.447.464h-1.369c-.298 0-.447-.155-.447-.464v-7.779a3.226 3.226 0 01.996-2.378 3.29 3.29 0 012.404-.987 3.355 3.355 0 012.284.858 3.357 3.357 0 012.284-.858 3.29 3.29 0 012.404.987 3.227 3.227 0 01.996 2.378v7.779z" fill="#fff" fill-rule="nonzero"/><path fill="none" stroke="#fff" stroke-width="2" d="M1 1h46v46H1z"/></svg>

			<app-logo v-else key="header-logo" hover class="app-header__logo-basic" />
		</router-link>

		<template v-if="!simplified">
			<slot name="tools" />

			<div class="app-header__menu-wrapper">
				<user-menu
					v-if="isAuthorized"
					class="app-header__user-menu"
					:show-user-name="showUserName"
				/>
			</div>
		</template>
	</header>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import AppLogo from '@/components/app/AppLogo.vue';
import UserMenu from '@/components/app/header/user/Menu.vue';
import { authGetters } from '@/store/modules/auth';

export default defineComponent({
	components: {
		AppLogo,
		UserMenu,
	},
	props: {
		simplified: {
			type: Boolean,
			default: false,
		},
		showUserName: {
			type: Boolean,
			default: true,
		},
		variant: {
			type: String as PropType<'standard' | 'wide'>,
			default: 'standard',
			validator: (value: string) => ['standard', 'wide'].includes(value),
		},
	},
	computed: {
		...authGetters.map(['isAuthorized']),

		logoTo(): RouteLocationRaw {
			return this.simplified ? { name: 'home' } : { name: 'dashboard' };
		},
	},
});
</script>

<style lang="scss">
:root {
	--header-height: 11rem;

	@include max-lg-and-portrait {
		--header-height: 9.2rem;
	}
}

.app-header {
	display: grid;
	grid-template-columns: repeat(24, 1fr);
	grid-template-rows: 1fr;
	max-width: 100vw;
	height: $header-height;
	padding: 0.5rem 4.8rem;
	position: fixed;
	top: 0;
	right: 0;
	left: 0;
	z-index: 10000;
	background: $color-white;
	gap: 0 2.4rem;

	@include max-lg-and-portrait {
		padding: 0.5rem 3rem;
		gap: 0 2rem;
	}

	&__logo {
		display: flex;
		grid-column: 1 / span 2;
		align-self: center;
		min-width: 5.6rem;
		outline: 0;

		@include max-lg-and-portrait {
			min-width: 4.4rem;
		}
	}

	&__logo-basic {
		width: 5.6rem;
		height: 5.6rem;

		@include max-lg-and-portrait {
			width: 4.4rem;
			height: 4.4rem;
		}
	}

	&__logo-simplified {
		width: 4.8rem;
		height: 4.8rem;

		@include max-lg-and-portrait {
			width: 4.4rem;
			height: 4.4rem;
		}
	}

	/* Standard variant (Admin/User) - original layout from master */
	&__menu-wrapper {
		display: flex;
		grid-column: 24 / span 1;
		align-items: center;
		position: relative;
	}

	/* Wide variant (Production Manager) - with space for navigation tabs */
	&--wide {
		& > *:not(.app-header__logo):not(.app-header__menu-wrapper) {
			display: flex;
			grid-column: 3 / span 17;
			align-items: center;
			justify-content: center;
			width: 100%;

			@include max-lg-and-portrait {
				grid-column: 3 / span 15;
			}
		}

		.app-header__menu-wrapper {
			grid-column: 20 / span 5;
			justify-content: flex-end;

			@include max-lg-and-portrait {
				grid-column: 18 / span 7;
			}
		}
	}

	&__user-menu {
		right: 0;

		&.base-dropdown {
			position: absolute;
		}
	}

	&--simplified {
		justify-content: flex-start;
		background: transparent;
	}
}
</style>
